// ScrollPinReveal.js - Scroll-pinned sequential item reveal animation


(function () {
    function register(Alpine) {
        // gsap + ScrollTrigger come from the shared lib/gsap.min.js chunk (see lib/gsap.js).
        // Resolved here, at Alpine init, rather than at module load: both scripts are async.
        const gsap = window.gsap;
        const ScrollTrigger = window.ScrollTrigger;
        if (!gsap || !ScrollTrigger) {
            console.warn('[ScrollPinReveal] gsap not available - include Resources/Public/js/lib/gsap.min.js before this script');
            return;
        }
        if (Alpine.__scrollPinRevealRegistered) return;
        Alpine.__scrollPinRevealRegistered = true;

        /**
         * Alpine.js component for scroll-pinned reveal animation
         *
         * Usage: x-data="scrollPinReveal()" with data attributes:
         *   data-scroll-pin-effect="fade|slideLeft|slideRight|slideUp|scale"
         *   data-scroll-pin-unpin="0|1|2"
         */
        Alpine.data('scrollPinReveal', (opts = {}) => ({
            // Configuration
            effect: null,
            unpin: 0,
            itemSelector: opts.itemSelector || '.scroll-pin-item',

            // Internals
            _scrollTrigger: null,
            _timeline: null,
            _items: [],
            _reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,

            init() {
                // Read configuration from data attributes
                this.effect = this.$root.dataset.scrollPinEffect || opts.effect || 'fade';
                this.unpin = parseInt(this.$root.dataset.scrollPinUnpin ?? opts.unpin ?? 0, 10);

                this.$nextTick(() => {
                    this._items = Array.from(this.$root.querySelectorAll(this.itemSelector));

                    if (this._items.length === 0) {
                        return;
                    }

                    if (this._reduced) {
                        this._items.forEach(item => {
                            item.style.opacity = '1';
                            item.style.transform = 'none';
                        });
                        return;
                    }

                    this._setupAnimation();
                });
            },

            destroy() {
                if (this._scrollTrigger) {
                    this._scrollTrigger.kill();
                }
                if (this._timeline) {
                    this._timeline.kill();
                }
                ScrollTrigger.refresh();
            },

            _setupAnimation() {
                const container = this.$root;
                const items = this._items;
                const itemCount = items.length;

                // Shorter scroll distance per item for snappier feel
                const scrollPerItem = 120;
                const totalScrollDistance = scrollPerItem * itemCount;

                // Calculate extra scroll based on unpin behavior
                let extraScroll = 0;
                if (this.unpin === 1) {
                    extraScroll = 100; // Short pause
                } else if (this.unpin === 2) {
                    extraScroll = 200; // Scroll-out distance
                }

                const endOffset = totalScrollDistance + extraScroll;

                // Set initial state for all items
                items.forEach(item => {
                    gsap.set(item, this._getInitialState());
                });

                // Create timeline with staggered animations
                this._timeline = gsap.timeline({ paused: true });

                items.forEach((item, index) => {
                    // Each item starts and completes within its portion of the scroll
                    const startPosition = index / itemCount;
                    const duration = 0.8 / itemCount; // Overlap animations slightly

                    this._timeline.to(item, {
                        ...this._getFinalState(),
                        duration: duration,
                        ease: 'power3.out',
                    }, startPosition);
                });

                // Create ScrollTrigger - pin when section center reaches viewport center
                this._scrollTrigger = ScrollTrigger.create({
                    trigger: container,
                    start: 'center center',
                    end: `+=${endOffset}`,
                    pin: true,
                    pinSpacing: true,
                    scrub: 0.3, // Faster response
                    anticipatePin: 1,
                    onUpdate: (self) => {
                        const revealPortion = totalScrollDistance / endOffset;
                        const progress = Math.min(self.progress / revealPortion, 1);
                        this._timeline.progress(progress);

                        // Handle scroll-out effect
                        if (this.unpin === 2 && self.progress > revealPortion) {
                            const exitProgress = (self.progress - revealPortion) / (1 - revealPortion);
                            this._applyScrollOutEffect(exitProgress);
                        }
                    },
                    onLeave: () => {
                        this._timeline.progress(1);
                        if (this.unpin === 2) {
                            this._resetScrollOutEffect();
                        }
                    },
                    onEnterBack: () => {
                        if (this.unpin === 2) {
                            this._resetScrollOutEffect();
                        }
                    }
                });
            },

            _getInitialState() {
                const base = { opacity: 0, willChange: 'transform, opacity' };
                switch (this.effect) {
                    case 'slideLeft':
                        return { ...base, x: 60, y: 20 };
                    case 'slideRight':
                        return { ...base, x: -60, y: 20 };
                    case 'slideUp':
                        return { ...base, y: 40 };
                    case 'scale':
                        return { ...base, scale: 0.85, y: 20 };
                    case 'fade':
                    default:
                        return { ...base, y: 30 };
                }
            },

            _getFinalState() {
                return { opacity: 1, x: 0, y: 0, scale: 1 };
            },

            _applyScrollOutEffect(progress) {
                const eased = gsap.parseEase('power2.in')(progress);
                gsap.set(this.$root, {
                    opacity: 1 - eased * 0.4,
                    scale: 1 - eased * 0.03,
                    y: eased * -20
                });
            },

            _resetScrollOutEffect() {
                gsap.set(this.$root, {
                    opacity: 1,
                    scale: 1,
                    y: 0
                });
            }
        }));
    }

    if (window.Alpine) {
        register(window.Alpine);
    } else {
        document.addEventListener('alpine:init', () => register(window.Alpine));
    }
})();
